import {
  FC,
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useDispatch } from "react-redux";

import { IAPIState } from "shared/types";
import {
  DeleteBandInvitationModal,
  RevokeBandInvitationModal,
  SendBandInvitationModal,
} from "Bands/containers";
import { IBandInvitation } from "Bands/types";
import {
  dispatchGetPendingBandInvitationsAction,
  dispatchGetSentBandInvitationsAction,
} from "Bands/state";
import { useBandInvitationIdFromPath, useGetBandInvitation } from "Bands/utils";
import { useBandContext } from ".";

interface BandInvitationContextState {
  invitationId: string | null;
  invitation: IBandInvitation | null;
  invitationApiMeta?: {
    status: IAPIState;
    error?: any;
  } | null;
  openSendModal: (
    invitation: IBandInvitation,
    type: InvitationActionSourceType
  ) => void;
  openRevokeModal: (invitation: IBandInvitation) => void;
  openDeleteModal: (
    invitation: IBandInvitation,
    type: InvitationActionSourceType
  ) => void;
  fetchSentInvitations: (page?: number) => void;
  fetchPendingInvitations: (page?: number) => void;
  invitationActionSource: InvitationActionSourceType | null;
  setInvitationActionSource: (type: InvitationActionSourceType) => void;
}

const initialState: BandInvitationContextState = {
  invitationId: null,
  invitation: null,
  invitationApiMeta: null,
  openSendModal: () => {},
  openRevokeModal: () => {},
  openDeleteModal: () => {},
  fetchSentInvitations: () => {},
  fetchPendingInvitations: () => {},
  invitationActionSource: null,
  setInvitationActionSource: () => {},
};

export enum InvitationActionSourceType {
  "pending",
  "sent",
}

const BandInvitationContext =
  createContext<BandInvitationContextState>(initialState);

export const MembersProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  let invitationId = useBandInvitationIdFromPath();
  let { bandId } = useBandContext();

  const { bandInvitationMeta: invitationApiMeta, bandInvitation: invitation } =
    useGetBandInvitation(invitationId);

  const [showSendModal, setShowSendModal] =
    useState<IBandInvitation | null>(null);
  const [showRevokeModal, setShowRevokeModal] =
    useState<IBandInvitation | null>(null);
  const [showDeleteModal, setShowDeleteModal] =
    useState<IBandInvitation | null>(null);
  const [invitationActionSource, setInvitationActionSource] =
    useState<InvitationActionSourceType | null>(null);

  // fetch sent invitations
  const fetchSentInvitations = useCallback(
    (page?: number) => {
      if (bandId) {
        dispatchGetSentBandInvitationsAction(dispatch, {
          band: bandId,
          page,
        });
      }
    },
    [bandId, dispatch]
  );
  useEffect(() => {
    fetchSentInvitations();
  }, [fetchSentInvitations]);

  // fetch pending invitations
  const fetchPendingInvitations = useCallback(
    (page?: number) => {
      if (bandId) {
        dispatchGetPendingBandInvitationsAction(dispatch, {
          band: bandId,
          page,
        });
      }
    },
    [bandId, dispatch]
  );
  useEffect(() => {
    fetchPendingInvitations();
  }, [fetchPendingInvitations]);

  return (
    <BandInvitationContext.Provider
      value={{
        invitationId,
        invitation,
        invitationApiMeta,
        openSendModal: (invitation, type) => {
          setShowSendModal(invitation);
          setInvitationActionSource(type);
        },
        openRevokeModal: (invitation) => setShowRevokeModal(invitation),
        openDeleteModal: (invitation, type) => {
          setShowDeleteModal(invitation);
          setInvitationActionSource(type);
        },
        fetchSentInvitations,
        fetchPendingInvitations,
        invitationActionSource,
        setInvitationActionSource,
      }}
    >
      {showSendModal && (
        <SendBandInvitationModal
          invitation={showSendModal}
          handleClose={() => {
            setShowSendModal(null);
            setInvitationActionSource(null);
          }}
        />
      )}
      {showRevokeModal && (
        <RevokeBandInvitationModal
          invitation={showRevokeModal}
          handleClose={() => {
            setShowRevokeModal(null);
            setInvitationActionSource(null);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteBandInvitationModal
          invitation={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(null);
            setInvitationActionSource(null);
          }}
        />
      )}
      {children}
    </BandInvitationContext.Provider>
  );
};

export const useBandInvitationContext = () => useContext(BandInvitationContext);
